import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { createSickLeaveTableColumns } from '../../components/sick-leave-table/sick-leave-table-columns';
import { DoctorLeaveRequestDialog } from '../../components/doctor-leave-request-dialog/doctor-leave-request-dialog';
import { useGetSickLeaveRequests } from '../../hooks/use-sick-leave';
import { DEFAULT_SICK_LEAVE_REQUESTS_ORDER } from '../../graphql/queries';
import { SICK_LEAVE_TITLE, SickLeaveRequestsPagingResult } from '../../types/sick-leave.types';

export const SickLeavePage = () => {
  const { t } = useTranslation();
  const canRequest = useHasPermission(HRM_PERMISSIONS.SICK_LEAVE.REQUEST);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SICK_LEAVE_REQUESTS_ORDER,
  });

  const filter = { Title: SICK_LEAVE_TITLE };
  const { data, isLoading, error } = useGetSickLeaveRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { LeaveRequests?: SickLeaveRequestsPagingResult })?.LeaveRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { byUserId } = useEmployeesByUserIds(items.map((item) => item.UserId));
  const emailOf = (userId: string) => byUserId.get(userId)?.Email;

  const columns = createSickLeaveTableColumns({ t, emailOf });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('SICK_LEAVE')}
        actions={
          canRequest
            ? [
                {
                  label: t('FILE_DOCTOR_CERTIFIED_SICK_LEAVE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setIsFormOpen(true),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <DoctorLeaveRequestDialog open={isFormOpen} onOpenChange={setIsFormOpen} />
    </div>
  );
};
